import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heading, Subheading } from '../../../../components/Heading';
import { Card } from '../../../../components/Card';
import { Input } from '../../../../components/Input';
import { Textarea } from '../../../../components/Textarea';
import { Button } from '../../../../components/Button';
import { UserContext } from '../../../../context/userContext';
import showErrorToast from '../../../../components/Toasts/ShowErrorToast';
import showLoadingToast from '../../../../components/Toasts/ShowLoadingToast';
import showSuccessToast from '../../../../components/Toasts/ShowSuccessToast';
import { AppReleaseService } from '../../../../services/appReleaseService';

const AddAppUpdatePage: React.FC = () => {
    const navigate = useNavigate();
    const { currentUser } = useContext(UserContext);
    const [loading, setLoading] = useState(false);
    const [versionName, setVersionName] = useState('');
    const [versionCode, setVersionCode] = useState('');
    const [releaseNotes, setReleaseNotes] = useState('');
    const [apkFile, setApkFile] = useState<File | null>(null);

    useEffect(() => {
        document.title = 'Add App Update';
    }, []);

    const formatFileSize = (bytes: number) => {
        if (!bytes || bytes <= 0) return '-';
        const mb = bytes / (1024 * 1024);
        return `${mb.toFixed(2)} MB`;
    };

    const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] || null;
        if (!file) {
            setApkFile(null);
            return;
        }

        if (!file.name.toLowerCase().endsWith('.apk')) {
            showErrorToast('Please select a valid APK file (.apk)');
            e.target.value = '';
            return;
        }

        setApkFile(file);
    };

    const validate = () => {
        if (!versionName.trim()) {
            showErrorToast('Version name is required');
            return false;
        }
        if (!versionCode.trim() || Number.isNaN(Number(versionCode)) || Number(versionCode) <= 0) {
            showErrorToast('Version code must be a positive number');
            return false;
        }
        if (!apkFile) {
            showErrorToast('APK file is required');
            return false;
        }
        return true;
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (loading) return;
        if (!validate()) return;

        try {
            setLoading(true);
            showLoadingToast('Uploading APK release...', 400);

            const formData = new FormData();
            formData.append('apk', apkFile as Blob);
            formData.append('version_name', versionName.trim());
            formData.append('version_code', versionCode.trim());
            formData.append('release_notes', releaseNotes.trim());
            if (currentUser?.id != null) {
                formData.append('created_user_id', String(currentUser.id));
            }

            const response = await AppReleaseService.uploadAndroidRelease(formData);
            if (response?.dbsuccess) {
                showSuccessToast('APK release uploaded successfully');
                navigate('/gp/app/updates');
            } else {
                showErrorToast(response?.message || 'Upload failed');
            }
        } catch (error: any) {
            showErrorToast(error?.response?.data?.message || 'Failed to upload APK release');
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="mx-auto max-w-6xl">
            <div className="lg:flex lg:items-center lg:justify-between mb-5">
                <Heading>Add App Update</Heading>
                <div className="mt-5 flex lg:ml-4 lg:mt-0">
                    <Button type="reset" outline onClick={() => navigate(-1)}>
                        Cancel
                    </Button>
                    <Button type="submit" className="ml-3" disabled={loading}>
                        {loading ? 'Uploading...' : 'Save'}
                    </Button>
                </div>
            </div>
            <Card className="mt-6 p-6">
                <div className="grid grid-cols-1 gap-x-8 gap-y-10 md:grid-cols-3">
                    <div>
                        <h2 className="text-base font-semibold leading-7 text-foreground">Release Details</h2>
                        <p className="mt-1 text-sm leading-6 text-muted-foreground">
                            Upload a new Android APK release with version metadata.
                        </p>
                    </div>
                    <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                        <div className="sm:col-span-full">
                            <Subheading>Version Name</Subheading>
                            <div className="mt-2">
                                <Input
                                    type="text"
                                    value={versionName}
                                    onChange={(e) => setVersionName(e.target.value)}
                                    placeholder="e.g. 2.1.0"
                                />
                            </div>
                        </div>
                        <div className="sm:col-span-full">
                            <Subheading>Version Code</Subheading>
                            <div className="mt-2">
                                <Input
                                    type="number"
                                    value={versionCode}
                                    onChange={(e) => setVersionCode(e.target.value)}
                                    placeholder="e.g. 210"
                                />
                            </div>
                        </div>
                        <div className="sm:col-span-full">
                            <Subheading>Release Notes</Subheading>
                            <div className="mt-2">
                                <Textarea
                                    value={releaseNotes}
                                    onChange={(e) => setReleaseNotes(e.target.value)}
                                    placeholder="What is new in this release"
                                />
                            </div>
                        </div>
                        <div className="sm:col-span-full">
                            <Subheading>APK File</Subheading>
                            <div className="mt-2">
                                <Input type="file" accept=".apk" onChange={onFileChange} />
                                {apkFile && (
                                    <p className="mt-2 text-sm text-zinc-500">
                                        Selected: {apkFile.name} ({formatFileSize(apkFile.size)})
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </Card>
        </form>
    );
};

export default AddAppUpdatePage;
